import { Controller } from "@hotwired/stimulus"

// Manages the 4-step AI search progress wizard.
//
// Two paths advance the wizard:
//
//  1. Server broadcast (auto-advance):
//     Jobs call broadcast_replace_to on the wizard container, re-rendering it
//     with the new current_step value. The active step panel is rendered with
//     `animate-wizard-step-enter` baked into the class attribute, so the CSS
//     animation fires the instant Turbo inserts the new element — no JS needed.
//
//  2. Manual click (user revisits a completed step):
//     goToStep() changes stepValue → stepValueChanged() calls _animateActivePanel()
//     to play the same animation client-side, keeping both paths visually identical.
export default class extends Controller {
  static values = {
    step:       { type: Number, default: 1 },
    totalSteps: { type: Number, default: 4 },
    maxStep:    { type: Number, default: 1 }
  }
  static targets = ["stepPanel", "stepIndicator", "progressFilled", "progressGhost"]

  connect() {
    this._initialized = true
    this.updateUI()
    // No _animateActivePanel() here — the server already put animate-wizard-step-enter
    // on the active panel, so the CSS animation fires on DOM insertion automatically.
  }

  stepValueChanged() {
    this.updateUI()
    // Only animate after the controller is fully connected, and only for user-driven
    // step changes (not the initial value callback that fires before connect()).
    if (this._initialized) this._animateActivePanel()
  }

  // Called when a step header is clicked. Only navigates if the target step
  // has already been reached (i.e., step <= maxStepValue).
  goToStep(event) {
    const target = parseInt(event.currentTarget.dataset.step, 10)
    if (target >= 1 && target <= this.maxStepValue) {
      this.stepValue = target   // triggers stepValueChanged → updateUI + animate
    }
  }

  updateUI() {
    const step  = this.stepValue
    const total = this.totalStepsValue

    // Show only the active step panel
    this.stepPanelTargets.forEach((panel) => {
      const panelStep = parseInt(panel.dataset.step, 10)
      panel.classList.toggle("hidden", panelStep !== step)
    })

    // Update step indicator circles
    this.stepIndicatorTargets.forEach((indicator) => {
      const n         = parseInt(indicator.dataset.step, 10)
      const circle    = indicator.querySelector("[data-role='circle']")
      const checkIcon = indicator.querySelector("[data-role='check']")
      const numSpan   = indicator.querySelector("[data-role='num']")

      if (circle) {
        circle.classList.toggle("bg-primary",           n <= step)
        circle.classList.toggle("text-primary-content", n <= step)
        circle.classList.toggle("bg-base-200",          n > step)
        circle.classList.toggle("text-base-content",    n > step)
      }
      if (checkIcon) checkIcon.classList.toggle("hidden", n >= step)
      if (numSpan)   numSpan.classList.toggle("hidden",   n < step)
    })

    // Update progress bars (completed fill + in-progress ghost)
    const completedScale  = (step - 1) / total
    const inProgressScale = step / total

    this.progressFilledTargets.forEach(el => {
      el.style.transform = `scaleX(${completedScale})`
    })
    this.progressGhostTargets.forEach(el => {
      el.style.transform = `scaleX(${inProgressScale})`
    })
  }

  // Plays the enter animation on the currently active panel.
  // Used only for manual navigation (goToStep → stepValueChanged).
  // Auto-advance relies on the server-rendered animate-wizard-step-enter class.
  _animateActivePanel() {
    const panel = this.stepPanelTargets.find(
      p => parseInt(p.dataset.step, 10) === this.stepValue
    )
    if (!panel) return

    // Remove then re-add the class so the animation restarts even if the panel
    // already carries the class from a previous navigation.
    panel.classList.remove("animate-wizard-step-enter")
    void panel.offsetWidth  // force reflow so the browser registers the removal
    panel.classList.add("animate-wizard-step-enter")
    panel.addEventListener(
      "animationend",
      () => panel.classList.remove("animate-wizard-step-enter"),
      { once: true }
    )
  }
}
